import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { acceptLegal, login, logout, verifyLoggedIn } from './thunkActions/auth';
import { updateLang, updateSettings } from './thunkActions/settings';
import { IUserMessage, IUserState } from './types';

const getInitialState = (): IUserState => ({
    isLoggedIn: false,
    isLoggingIn: false,
    isLoggedInVerified: false,
    isSettingsUpdating: false,
    message: null,
    currentRequestId: undefined,
    user: null,
    isLoading: false,
});

export const userSlice = createSlice({
    name: 'user',
    initialState: getInitialState(),
    reducers: {
        setUserMessage: (state, action: PayloadAction<IUserMessage>) => ({ ...state, message: action.payload }),
    },
    extraReducers: builder => builder

        .addCase(updateLang.pending, state => ({ ...state, isSettingsUpdating: true }))

        .addCase(updateLang.rejected, state => ({ ...state, isSettingsUpdating: false }))

        .addCase(updateLang.fulfilled, (state, action) => ({ ...state, user: action.payload, isSettingsUpdating: false }))

        .addCase(updateSettings.pending, state => ({ ...state, isSettingsUpdating: true }))

        .addCase(updateSettings.rejected, state => ({ ...state, isSettingsUpdating: false }))

        .addCase(updateSettings.fulfilled, (state, action) => ({ ...state, user: action.payload, isSettingsUpdating: false }))

        .addCase(acceptLegal.pending, state => ({ ...state, isLoading: true }))

        .addCase(acceptLegal.rejected, state => ({ ...state, isLoading: false }))

        .addCase(acceptLegal.fulfilled, (state, action) => ({ ...state, user: action.payload, isLoading: false }))

        .addCase(login.pending, (state, action) => {
            if (!state.isLoggingIn) {
                return { ...state, isLoggedIn: false, isLoggingIn: true, currentRequestId: action.meta.requestId };
            }
            return state;
        })

        .addCase(login.rejected, (state, action) => {
            const { requestId } = action.meta;
            if (state.isLoggingIn && state.currentRequestId === requestId) {
                return { ...state, isLoggedIn: false, isLoggingIn: false, message: { text: action.error.message, isError: true }, currentRequestId: undefined };
            }
            return state;
        })

        .addCase(login.fulfilled, (state, action) => {
            const { requestId } = action.meta;
            if (state.isLoggingIn && state.currentRequestId === requestId) {
                return { ...state, isLoggedIn: true, isLoggingIn: false, user: action.payload, currentRequestId: undefined };
            }
            return state;
        })

        .addCase(logout.pending, (state, action) => {
            if (!state.isLoggingIn) {
                return { ...state, isLoggingIn: true, currentRequestId: action.meta.requestId };
            }
            return state;
        })

        .addCase(logout.rejected, (state, action) => {
            const { requestId } = action.meta;
            if (state.isLoggingIn && state.currentRequestId === requestId) {
                return { ...state, isLoggingIn: false, message: { text: action.error.message, isError: true }, currentRequestId: undefined };
            }
            return state;
        })

        .addCase(logout.fulfilled, state => ({ ...state, isLoggedIn: false, isLoggingIn: false, user: null, currentRequestId: undefined }))

        .addCase(verifyLoggedIn.pending, (state, action) => ({ ...state, isLoggedInVerified: false, isLoggingIn: true, currentRequestId: action.meta.requestId }))

        .addCase(verifyLoggedIn.rejected, state => ({
            ...state,
            isLoggingIn: false,
            isLoggedInVerified: true,
            user: null,
            isLoggedIn: false,
            currentRequestId: undefined,
        }))

        .addCase(verifyLoggedIn.fulfilled, (state, action) => ({
            ...state,
            isLoggingIn: false,
            isLoggedInVerified: true,
            user: action.payload,
            isLoggedIn: !!action.payload,
            currentRequestId: undefined,
        }))
    ,
});

export const { setUserMessage } = userSlice.actions;

export const user = userSlice.reducer;
